import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'
import { OpalApiResponseDocument, services } from '@opallabs/api/v2'
import { queryKeys } from './queryKeys'
import { FilterOption, isFilterOption } from './types'

const isValidAccount = (
  include?: Record<string, unknown>
): include is OpalApiResponseDocument<'ReadAccountV2', 200>['data'] =>
  include !== undefined && include.type === 'account'

const channelCategoryUuid = uuidv4()
const allAvailableChannelsUuid = uuidv4()

const serviceFilter = ({
  color,
  id,
  uuid,
  name,
  systemName,
  gemIcon,
}: {
  color: string
  id: string
  uuid: string
  name: string
  systemName: string
  gemIcon?: string
}): FilterOption => ({
  color,
  divider: false,
  groupOrAble: false,
  icon: `channel-${systemName}-14`,
  gemIcon,
  id: `service-${id}`,
  uuid,
  inclusivity: false,
  title: `Any ${name} account`,
  payload: {
    type_key: 'services_and_accounts',
    object: {
      id: systemName,
      _id: typeof id === 'string' ? parseInt(id, 10) : id,
    },
  },
})

export const useChannelFilters = ({
  enabled,
  workspaceId,
}: {
  enabled: boolean
  workspaceId: number
}): UseQueryResult<FilterOption | null> =>
  useQuery({
    queryKey: queryKeys.channelFilters(workspaceId),
    queryFn: () =>
      services.read({
        params: {
          query: {
            filter: { brand_id: ['null', workspaceId.toString()] },
            include: ['accounts'],
          },
        },
      }),
    enabled,
    select: (data): FilterOption | null => {
      const channels = data.data

      if (!Array.isArray(channels) || channels.length === 0) {
        return null
      }

      const channelsWithAccounts = channels.filter(
        (channel) =>
          Array.isArray(channel.relationships.accounts.data) &&
          channel.relationships.accounts.data.length > 0
      )
      const channelsWithoutAccounts = channels.filter(
        (channel) =>
          !Array.isArray(channel.relationships.accounts.data) ||
          channel.relationships.accounts.data.length === 0
      )

      const includedAccounts = data?.included?.filter(isValidAccount)
      return {
        divider: false,
        groupOrAble: false,
        id: `channels-and-accounts-${channelCategoryUuid}`,
        uuid: channelCategoryUuid,
        inclusivity: false,
        title: 'Channels & Accounts',
        icon: 'channel-all-14',
        gemIcon: 'gem-channel-all',
        childOptions: [
          ...channelsWithAccounts.map(
            ({ id: channelId, attributes, relationships }) => {
              const accountRelationships = relationships.accounts.data
              const channelIcon = `channel-${attributes.system_name}-14`
              const gemIcon = attributes.icon_name?.startsWith('web')
                ? 'gem-channel-web'
                : `gem-channel-${attributes.icon_name}`
              return {
                divider: false,
                groupOrAble: false,
                inclusivity: false,
                icon: channelIcon,
                gemIcon,
                id: `channel-${channelId}`,
                uuid: attributes.uuid,
                title: attributes.name,
                color: attributes.color,
                childOptions: [
                  serviceFilter({
                    color: attributes.color,
                    systemName: attributes.system_name,
                    id: channelId,
                    name: attributes.name,
                    uuid: attributes.uuid,
                    gemIcon,
                  }),
                  ...accountRelationships
                    .map<FilterOption | null>(({ id: accountId }) => {
                      const accountData = includedAccounts?.find(
                        (acct) =>
                          acct.type === 'account' && acct.id === accountId
                      )
                      if (accountData === undefined) return null

                      return {
                        color: attributes.color,
                        divider: false,
                        groupOrAble: false,
                        icon: channelIcon,
                        gemIcon,
                        id: `account-${accountId}`,
                        uuid: accountData.attributes.uuid,
                        inclusivity: false,
                        title: accountData.attributes.name,
                        payload: {
                          type_key: 'services_and_accounts',
                          object: {
                            id:
                              typeof accountId === 'string'
                                ? parseInt(accountId, 10)
                                : accountId,
                            service_id:
                              typeof channelId === 'string'
                                ? parseInt(channelId, 10)
                                : channelId,
                          },
                        },
                      }
                    })
                    .filter<FilterOption>(isFilterOption),
                ],
              }
            }
          ),
          {
            divider: true,
            groupOrAble: false,
            inclusivity: false,
            id: 'all-available-channels',
            uuid: allAvailableChannelsUuid,
            title: 'All Available Channels',
          },
          ...channelsWithoutAccounts.map(({ id: channelId, attributes }) => {
            const gemIcon = attributes.icon_name?.startsWith('web')
              ? 'gem-channel-web'
              : `gem-channel-${attributes.icon_name}`
            return {
              divider: false,
              groupOrAble: false,
              inclusivity: false,
              icon: `channel-${attributes.system_name}-14`,
              gemIcon,
              id: `channel-${channelId}`,
              uuid: attributes.uuid,
              title: attributes.name,
              color: attributes.color,
              childOptions: [
                serviceFilter({
                  color: attributes.color,
                  id: channelId,
                  name: attributes.name,
                  systemName: attributes.system_name,
                  uuid: attributes.uuid,
                  gemIcon,
                }),
              ],
            }
          }),
        ],
      }
    },
  })
